/**
 * ViewSupportTicketDetail - Ticket detail page with comments and admin actions
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    ArrowDownUp,
    ChevronDown,
    ExternalLink,
    ImageOff,
    ListFilter,
    Pencil,
} from "lucide-react";
import { Button } from "@/ui/atoms/Button";
import { cn } from "@/utils/utils";
import { formatRelativeTime } from "@/utils/relativeTime";
import type {
    TApiSupportTicketResponse,
    TApiSupportTicketCommentResponse,
    TApiSupportTicketType,
    TApiSupportTicketPriority,
    TApiSupportTicketStatus,
} from "@/apis/proda_finance/types";

const TYPE_COLORS: Record<string, string> = {
    bug: "bg-red-100 text-red-700",
    feature: "bg-purple-100 text-purple-700",
};

const TYPE_LABELS: Record<string, string> = {
    bug: "Bug",
    feature: "Feature",
};

const PRIORITY_COLORS: Record<string, string> = {
    low: "bg-gray-100 text-gray-700",
    normal: "bg-blue-100 text-blue-700",
    high: "bg-red-100 text-red-700",
};

const PRIORITY_LABELS: Record<string, string> = {
    low: "Low",
    normal: "Normal",
    high: "High",
};

const STATUS_LABELS: Record<string, string> = {
    open: "Open",
    in_review: "In Review",
    needs_info: "Needs Info",
    rejected: "Rejected",
    forwarded: "Forwarded",
    closed: "Closed",
};

const ADMIN_STATUSES: TApiSupportTicketStatus[] = [
    "open",
    "in_review",
    "needs_info",
    "rejected",
    "forwarded",
    "closed",
];

const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;

/** Comment timestamps go absolute after three days — the support thread's own rule. */
function timeAgo(dateStr: string): { label: string; full: string } {
    return formatRelativeTime(dateStr, { maxRelativeMs: THREE_DAYS_MS });
}

function statusColor(status: string) {
    if (status === "closed") return "bg-green-100 text-green-700";
    if (status === "rejected") return "bg-red-100 text-red-700";
    return "bg-blue-100 text-blue-700";
}

function PillSelect<T extends string>({
    value,
    options,
    colorFn,
    onChange,
    disabled,
}: {
    value: T;
    options: { value: T; label: string }[];
    colorFn: (v: T) => string;
    onChange: (v: T) => void;
    disabled?: boolean;
}) {
    return (
        <div className="relative inline-flex">
            <select
                value={value}
                onChange={(e) => onChange(e.target.value as T)}
                disabled={disabled}
                className={cn(
                    "appearance-none rounded-full pl-2.5 pr-5 py-0.5 text-xs font-medium",
                    "cursor-pointer border-0 outline-none focus:ring-2 focus:ring-ring",
                    "disabled:opacity-50 disabled:cursor-default",
                    colorFn(value),
                )}
            >
                {options.map((o) => (
                    <option key={o.value} value={o.value}>
                        {o.label}
                    </option>
                ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 h-3 w-3 opacity-50" />
        </div>
    );
}

function PillBadge({ color, label }: { color: string; label: string }) {
    return (
        <span
            className={cn(
                "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                color,
            )}
        >
            {label}
        </span>
    );
}

export type TViewSupportTicketDetailParams = {
    useCases: () => {
        loadTicket: (id: string) => Promise<TApiSupportTicketResponse>;
        loadComments: (id: string) => Promise<TApiSupportTicketCommentResponse[]>;
        addComment: (id: string, body: string) => Promise<void>;
        closeTicket: (id: string) => Promise<void>;
        updateTicket: (
            id: string,
            data: {
                title?: string;
                description?: string;
                ticket_type?: TApiSupportTicketType;
                priority?: TApiSupportTicketPriority;
            },
        ) => Promise<void>;
        adminAction: (
            id: string,
            action: "reopen" | "in_review" | "needs_info" | "reject" | "forward",
        ) => Promise<void>;
        isAdmin: boolean;
        isProdaAdmin: boolean;
        currentUserId: string | null;
        /** Whether the user may edit ticket fields (report_bug_edit; Product Owner). */
        canManageReports: boolean;
    };
};

export type TViewSupportTicketDetailProps = Record<string, never>;

export const createViewSupportTicketDetail = ({ useCases }: TViewSupportTicketDetailParams) => {
    return function ViewSupportTicketDetail() {
        const { ticketId } = useParams<{ ticketId: string }>();
        const navigate = useNavigate();
        const {
            loadTicket,
            loadComments,
            addComment,
            closeTicket,
            updateTicket,
            adminAction,
            isAdmin,
            isProdaAdmin,
            currentUserId,
            canManageReports,
        } = useCases();

        const [ticket, setTicket] = useState<TApiSupportTicketResponse | null>(null);
        const [comments, setComments] = useState<TApiSupportTicketCommentResponse[]>([]);
        const [isLoading, setIsLoading] = useState(true);
        const [error, setError] = useState<string | null>(null);
        const [commentText, setCommentText] = useState("");
        const [isSubmitting, setIsSubmitting] = useState(false);
        const [isEditing, setIsEditing] = useState(false);
        const [editTitle, setEditTitle] = useState("");
        const [editDescription, setEditDescription] = useState("");

        const [showChanges, setShowChanges] = useState(() => {
            const stored = localStorage.getItem("ticket-show-changes");
            return stored === null ? true : stored === "true";
        });
        const [newestFirst, setNewestFirst] = useState(() => {
            return localStorage.getItem("ticket-newest-first") === "true";
        });
        const [screenshotOpen, setScreenshotOpen] = useState(false);
        const [screenshotError, setScreenshotError] = useState(false);
        // eslint-disable-next-line no-undef
        const lightboxRef = useRef<HTMLDialogElement>(null);
        const activityRef = useRef<HTMLDivElement>(null);

        const isOwner = ticket?.created_by_user_id === currentUserId;
        // Editing title/description/priority is gated by report_bug_edit, matching
        // the update_ticket endpoint (Product Owner, company-scoped) — not ownership.
        const canEdit = canManageReports;

        const scrollToNewest = useCallback((isNewest: boolean) => {
            window.requestAnimationFrame(() => {
                const el = activityRef.current;
                if (!el) return;
                if (isNewest) {
                    el.scrollTop = 0;
                } else {
                    el.scrollTop = el.scrollHeight;
                }
            });
        }, []);

        const visibleComments = useMemo(() => {
            let items = showChanges ? comments : comments.filter((c) => !c.is_admin_action);
            if (newestFirst) {
                items = [...items].reverse();
            }
            return items;
        }, [comments, showChanges, newestFirst]);

        useEffect(() => {
            scrollToNewest(newestFirst);
        }, [visibleComments, scrollToNewest, newestFirst]);

        const fetchData = useCallback(async () => {
            if (!ticketId) return;
            setIsLoading(true);
            setError(null);
            try {
                const [t, c] = await Promise.all([loadTicket(ticketId), loadComments(ticketId)]);
                setTicket(t);
                setComments(c);
                setEditTitle(t.title);
                setEditDescription(t.description);
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to load ticket");
            } finally {
                setIsLoading(false);
            }
        }, [ticketId, loadTicket, loadComments]);

        useEffect(() => {
            fetchData();
        }, [fetchData]);

        const handleAction = useCallback(
            async (fn: () => Promise<void>) => {
                setIsSubmitting(true);
                try {
                    await fn();
                    await fetchData();
                } finally {
                    setIsSubmitting(false);
                }
            },
            [fetchData],
        );

        const handleAddComment = async () => {
            if (!ticketId || !commentText.trim()) return;
            await handleAction(async () => {
                await addComment(ticketId, commentText.trim());
                setCommentText("");
            });
        };

        const handleSaveEdit = async () => {
            if (!ticketId) return;
            await handleAction(async () => {
                await updateTicket(ticketId, {
                    title: editTitle,
                    description: editDescription,
                });
                setIsEditing(false);
            });
        };

        const handleStatusChange = (newStatus: TApiSupportTicketStatus) => {
            if (!ticketId || newStatus === ticket?.status) return;
            const statusToAction: Record<
                string,
                "reopen" | "in_review" | "needs_info" | "reject" | "forward"
            > = {
                open: "reopen",
                in_review: "in_review",
                needs_info: "needs_info",
                rejected: "reject",
                forwarded: "forward",
            };
            if (newStatus === "closed") {
                handleAction(() => closeTicket(ticketId));
            } else if (statusToAction[newStatus]) {
                handleAction(() => adminAction(ticketId, statusToAction[newStatus]!));
            }
        };

        const canChangeStatus = isOwner || isAdmin;

        const statusOptions = (() => {
            if (!ticket) return [];
            if (isAdmin) {
                return ADMIN_STATUSES.map((s) => ({ value: s, label: STATUS_LABELS[s] || s }));
            }
            if (isOwner) {
                const current = ticket.status as TApiSupportTicketStatus;
                return [
                    { value: current, label: STATUS_LABELS[current] || current },
                    { value: "closed" as TApiSupportTicketStatus, label: "Closed" },
                ];
            }
            return [];
        })();

        if (isLoading) {
            return (
                <div className="flex items-center justify-center py-12">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                </div>
            );
        }

        if (error || !ticket) {
            return (
                <div className="space-y-4">
                    <Button variant="ghost" onClick={() => navigate("/support")}>
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back to tickets
                    </Button>
                    <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                        {error || "Ticket not found"}
                    </div>
                </div>
            );
        }

        return (
            <div className="flex flex-col gap-6 h-[calc(100vh-8rem)]">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/support")}
                    className="self-start shrink-0"
                >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to tickets
                </Button>

                {/* Title row */}
                {isEditing ? (
                    <div className="space-y-3">
                        <input
                            type="text"
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-lg font-semibold"
                        />
                        <textarea
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            rows={4}
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        />
                        <div className="flex gap-2">
                            <Button size="sm" onClick={handleSaveEdit} disabled={isSubmitting}>
                                Save
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                    setIsEditing(false);
                                    setEditTitle(ticket.title);
                                    setEditDescription(ticket.description);
                                }}
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center gap-2">
                        <h1 className="text-2xl font-semibold">{ticket.title}</h1>
                        {canEdit && (
                            <button
                                onClick={() => setIsEditing(true)}
                                className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                title="Edit ticket"
                            >
                                <Pencil className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                )}

                {/* Two-column layout */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1 min-h-0">
                    {/* Left column: description, comments */}
                    <div className="lg:col-span-2 flex flex-col gap-6 min-h-0">
                        {!isEditing && (
                            <div className="rounded-md border p-4">
                                <p className="text-sm whitespace-pre-wrap">{ticket.description}</p>
                            </div>
                        )}

                        {/* Screenshot below description */}
                        {!isEditing && ticket.has_screenshot && (
                            <div>
                                {ticket.screenshot_url && !screenshotError ? (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setScreenshotOpen(true);
                                            window.requestAnimationFrame(() =>
                                                lightboxRef.current?.showModal(),
                                            );
                                        }}
                                        className="block cursor-pointer rounded-md border border-border overflow-hidden hover:ring-2 hover:ring-primary transition-shadow"
                                    >
                                        <img
                                            src={ticket.screenshot_url}
                                            alt="Screenshot at time of report"
                                            className="max-h-64 object-contain object-left-top"
                                            onError={() => setScreenshotError(true)}
                                        />
                                    </button>
                                ) : (
                                    <div className="flex items-center gap-2 rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">
                                        <ImageOff className="h-4 w-4 shrink-0" />
                                        <span>Screenshot unavailable</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Activity */}
                        <div className="flex flex-col min-h-0 flex-1">
                            <div className="flex items-center justify-between py-2 shrink-0">
                                <h3 className="font-medium text-sm">Activity</h3>
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() =>
                                            setShowChanges((v) => {
                                                const next = !v;
                                                localStorage.setItem(
                                                    "ticket-show-changes",
                                                    String(next),
                                                );
                                                return next;
                                            })
                                        }
                                        title={showChanges ? "Hide changes" : "Show changes"}
                                        className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                    >
                                        <ListFilter className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                        onClick={() =>
                                            setNewestFirst((v) => {
                                                const next = !v;
                                                localStorage.setItem(
                                                    "ticket-newest-first",
                                                    String(next),
                                                );
                                                return next;
                                            })
                                        }
                                        title={newestFirst ? "Oldest first" : "Newest first"}
                                        className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                    >
                                        <ArrowDownUp className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            </div>
                            <div ref={activityRef} className="flex-1 overflow-y-auto space-y-3">
                                {visibleComments.length === 0 ? (
                                    <p className="text-sm text-muted-foreground">
                                        No activity yet.
                                    </p>
                                ) : (
                                    visibleComments.map((comment) =>
                                        comment.is_admin_action ? (
                                            <div
                                                key={comment.id}
                                                className="flex items-center gap-3 py-1 text-xs text-muted-foreground/60"
                                            >
                                                <div className="h-px flex-1 bg-border/40" />
                                                <span className="whitespace-nowrap">
                                                    {comment.author_display_name} {comment.body} ·{" "}
                                                    <span title={timeAgo(comment.created_at).full}>
                                                        {timeAgo(comment.created_at).label}
                                                    </span>
                                                </span>
                                                <div className="h-px flex-1 bg-border/40" />
                                            </div>
                                        ) : (
                                            <div key={comment.id} className="rounded-md border p-3">
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="text-sm font-medium">
                                                        {comment.author_display_name}
                                                    </span>
                                                    <span
                                                        className="text-xs text-muted-foreground"
                                                        title={timeAgo(comment.created_at).full}
                                                    >
                                                        {timeAgo(comment.created_at).label}
                                                    </span>
                                                </div>
                                                <p className="text-sm whitespace-pre-wrap">
                                                    {comment.body}
                                                </p>
                                            </div>
                                        ),
                                    )
                                )}
                            </div>
                            {
                                <div className="flex gap-2 items-start pt-4 border-t shrink-0">
                                    <textarea
                                        value={commentText}
                                        onChange={(e) => setCommentText(e.target.value)}
                                        placeholder="Add a comment..."
                                        rows={2}
                                        className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                    />
                                    <Button
                                        size="sm"
                                        onClick={handleAddComment}
                                        disabled={!commentText.trim() || isSubmitting}
                                    >
                                        Add Comment
                                    </Button>
                                </div>
                            }
                        </div>
                    </div>

                    {/* Right column: metadata */}
                    <div>
                        <div className="rounded-md border p-4 space-y-3 text-sm">
                            <div>
                                <span className="text-muted-foreground">Status</span>
                                <div className="mt-1">
                                    {canChangeStatus && statusOptions.length > 0 ? (
                                        <PillSelect
                                            value={ticket.status as TApiSupportTicketStatus}
                                            options={statusOptions}
                                            colorFn={statusColor}
                                            onChange={handleStatusChange}
                                            disabled={isSubmitting}
                                        />
                                    ) : (
                                        <PillBadge
                                            color={statusColor(ticket.status)}
                                            label={STATUS_LABELS[ticket.status] || ticket.status}
                                        />
                                    )}
                                </div>
                            </div>
                            <div>
                                <span className="text-muted-foreground">Type</span>
                                <div className="mt-1">
                                    {canEdit ? (
                                        <PillSelect
                                            value={ticket.ticket_type as TApiSupportTicketType}
                                            options={[
                                                { value: "bug", label: "Bug" },
                                                { value: "feature", label: "Feature" },
                                            ]}
                                            colorFn={(v) =>
                                                TYPE_COLORS[v] || "bg-gray-100 text-gray-700"
                                            }
                                            onChange={(v) =>
                                                handleAction(() =>
                                                    updateTicket(ticketId!, { ticket_type: v }),
                                                )
                                            }
                                            disabled={isSubmitting}
                                        />
                                    ) : (
                                        <PillBadge
                                            color={
                                                TYPE_COLORS[ticket.ticket_type] ||
                                                "bg-gray-100 text-gray-700"
                                            }
                                            label={
                                                TYPE_LABELS[ticket.ticket_type] ||
                                                ticket.ticket_type
                                            }
                                        />
                                    )}
                                </div>
                            </div>
                            <div>
                                <span className="text-muted-foreground">Priority</span>
                                <div className="mt-1">
                                    {canEdit ? (
                                        <PillSelect
                                            value={ticket.priority as TApiSupportTicketPriority}
                                            options={[
                                                { value: "low", label: "Low" },
                                                { value: "normal", label: "Normal" },
                                                { value: "high", label: "High" },
                                            ]}
                                            colorFn={(v) =>
                                                PRIORITY_COLORS[v] || "bg-gray-100 text-gray-700"
                                            }
                                            onChange={(v) =>
                                                handleAction(() =>
                                                    updateTicket(ticketId!, { priority: v }),
                                                )
                                            }
                                            disabled={isSubmitting}
                                        />
                                    ) : (
                                        <PillBadge
                                            color={
                                                PRIORITY_COLORS[ticket.priority] ||
                                                "bg-gray-100 text-gray-700"
                                            }
                                            label={
                                                PRIORITY_LABELS[ticket.priority] || ticket.priority
                                            }
                                        />
                                    )}
                                </div>
                            </div>
                            <div>
                                <span className="text-muted-foreground">Reporter</span>
                                <div className="mt-1">
                                    {ticket.created_by_display_name}
                                    <div className="text-xs text-muted-foreground">
                                        {ticket.created_by_email}
                                    </div>
                                </div>
                            </div>
                            <div>
                                <span className="text-muted-foreground">Created</span>
                                <div className="mt-1">
                                    {new Date(ticket.created_at).toLocaleString()}
                                </div>
                            </div>
                            {ticket.page_url && (
                                <div>
                                    <span className="text-muted-foreground">Page</span>
                                    <div className="mt-1 break-all text-xs">
                                        <a
                                            href={ticket.page_url}
                                            className="text-primary hover:underline"
                                        >
                                            {ticket.page_url}
                                        </a>
                                    </div>
                                </div>
                            )}
                            {ticket.has_screenshot && (
                                <div>
                                    <span className="text-muted-foreground">Screenshot</span>
                                    <div className="mt-1">
                                        {ticket.screenshot_url ? (
                                            <a
                                                href={ticket.screenshot_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                                            >
                                                Open screenshot
                                                <ExternalLink className="h-3 w-3" />
                                            </a>
                                        ) : (
                                            <span className="text-xs text-muted-foreground">
                                                Unavailable
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                            {ticket.company_name && isProdaAdmin && (
                                <div>
                                    <span className="text-muted-foreground">Company</span>
                                    <div className="mt-1">{ticket.company_name}</div>
                                </div>
                            )}
                            {ticket.forwarded_at && (
                                <div>
                                    <span className="text-muted-foreground">Forwarded</span>
                                    <div className="mt-1">
                                        {new Date(ticket.forwarded_at).toLocaleString()}
                                    </div>
                                </div>
                            )}
                            {ticket.closed_at && (
                                <div>
                                    <span className="text-muted-foreground">Closed</span>
                                    <div className="mt-1">
                                        {new Date(ticket.closed_at).toLocaleString()}
                                    </div>
                                </div>
                            )}
                            {ticket.admin_notes && (
                                <div className="pt-2 border-t">
                                    <span className="text-muted-foreground">Admin Notes</span>
                                    <div className="mt-1">{ticket.admin_notes}</div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Screenshot lightbox */}
                {screenshotOpen && ticket.screenshot_url && (
                    <dialog
                        ref={lightboxRef}
                        className="fixed inset-0 z-50 m-0 flex h-full w-full max-h-none max-w-none items-center justify-center bg-black/80 p-4 backdrop:bg-transparent"
                        onClick={(e) => {
                            if (e.target === e.currentTarget) {
                                lightboxRef.current?.close();
                                setScreenshotOpen(false);
                            }
                        }}
                        onClose={() => setScreenshotOpen(false)}
                    >
                        <img
                            src={ticket.screenshot_url}
                            alt="Screenshot at time of report"
                            className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain"
                        />
                    </dialog>
                )}
            </div>
        );
    };
};
